/**
 * ───────────────────────────────────────────────────────────────────────────
 * BRAND IDENTITY — SINGLE SOURCE OF TRUTH FOR THE WORDMARK
 * ───────────────────────────────────────────────────────────────────────────
 *
 * The platform brand is GIGGONE (all caps), not "GigGone".
 *
 * This module is deliberately dependency-free so that EVERY other module —
 * constants, i18n, SEO schema, email, legal copy — can import it without a
 * circular import. Nothing else in the codebase may hardcode the wordmark.
 *
 * WHY A NORMALISER AND NOT JUST A CONSTANT
 * A large amount of brand copy lives inside long prose sentences in the
 * bilingual dictionary, the SEO FAQ generators and the Help Center guides
 * ("... on GigGone the trade is public ..."). Retyping the brand in ~160
 * places is exactly how capitalisation drifts back apart over time. Instead,
 * every string leaving those data layers is passed through `brandCase()`,
 * which rewrites any legacy casing to the canonical wordmark. That means:
 *
 *   - There is ONE place to change if the brand is ever restyled again.
 *   - A future contributor who types "GigGone" out of habit still renders
 *     GIGGONE on screen, in meta tags, in JSON-LD and in email.
 *   - Domains, URLs and email addresses are NEVER touched (giggone.com must
 *     stay lowercase — DNS/mailbox identifiers are not branding).
 */

/** Canonical display wordmark. Use this instead of typing the brand. */
export const BRAND_NAME = 'GIGGONE';

/** Descriptor that follows the wordmark in legal / formal contexts. */
export const BRAND_SUFFIX = 'Marketplace';

/** Legal + schema.org name, e.g. "GIGGONE Marketplace". */
export const BRAND_FULL_NAME = `${BRAND_NAME} ${BRAND_SUFFIX}`;

/**
 * Lowercase domain. Domains are identifiers, not branding — they stay
 * lowercase everywhere (DNS, SPF/DKIM/DMARC, mailto:, canonical URLs).
 */
export const BRAND_DOMAIN_LOWER = 'giggone.com';

/**
 * Legacy / mixed-case spellings that must be normalised to the wordmark.
 * Matching is case-sensitive on purpose so that "giggone.com" inside a URL or
 * an email address is left completely alone.
 */
const LEGACY_SPELLINGS = /\bGigGone\b|\bGig Gone\b|\bGiggone\b/g;

/**
 * Rewrite any legacy spelling of the brand inside a string to GIGGONE.
 * Safe to call on any user-facing text: it never touches domains, email
 * addresses or URLs because those are lowercase and word-boundary guarded.
 */
export const brandCase = (input: string): string => {
  if (!input) return input;
  return input.replace(LEGACY_SPELLINGS, BRAND_NAME);
};

/** Apply `brandCase` to every string value of an object (shallow). */
export const brandCaseObject = <T extends Record<string, unknown>>(obj: T): T => {
  const out: Record<string, unknown> = {};
  Object.entries(obj).forEach(([k, v]) => {
    out[k] = typeof v === 'string' ? brandCase(v) : v;
  });
  return out as T;
};

/**
 * Two-tone wordmark parts for the logo lockup. The visual brand is a single
 * all-caps word with an accent dot, so the lockup renders GIGGONE + accent
 * rather than a camel-case split.
 */
export const WORDMARK = {
  text: BRAND_NAME,
  accent: '.',
} as const;
